import { create } from 'zustand'

/**
 * What the automatic visual review actually did, for the page in front of the
 * creator.
 *
 * It used to do none of this: the bridge called the review with `void` and
 * dropped the result, so an answer, an auth failure and a runtime that
 * advertises no visual-review skill were indistinguishable — all three looked
 * exactly like nothing happening. `PostA2AResult` names `unauthorized` as its
 * own case, and on a 30-page walk that was thirty silent failures.
 *
 * `null` means no review has run for what is on screen.
 */
export type PreviewReviewOutcomeKind =
  | 'reviewed'
  | 'unauthorized'
  | 'failed'
  | 'unavailable'
  | 'capture-failed'
  | 'surface-not-rendered'

export interface PreviewReviewOutcome {
  kind: PreviewReviewOutcomeKind
  /** `<slug>:<pageId>` — which page this outcome is about. */
  reviewKey: string
  /** Present for a transport failure, so the reason is not invented. */
  status?: number
}

interface PreviewReviewOutcomeState {
  outcome: PreviewReviewOutcome | null
  setOutcome: (outcome: PreviewReviewOutcome | null) => void
}

const useStore = create<PreviewReviewOutcomeState>((set) => ({
  outcome: null,
  setOutcome: (outcome) => set({ outcome }),
}))

export function usePreviewReviewOutcome(): PreviewReviewOutcome | null {
  return useStore((s) => s.outcome)
}

export const usePreviewReviewOutcomeStore = useStore

/**
 * Reads the review's own return value rather than guessing from it.
 *
 * `null` is the send's documented "the runtime advertises no visual-review
 * skill" answer, which is a fact about the deployment, not a failure.
 */
const GIVE_UP_KIND: Record<string, PreviewReviewOutcomeKind> = {
  'skill-not-advertised': 'unavailable',
  'surface-not-rendered': 'surface-not-rendered',
  'capture-failed': 'capture-failed',
}

export function outcomeFromResult(
  result:
    | { ok: boolean; unauthorized: boolean; status: number }
    | { giveUp: string }
    | null,
  reviewKey: string,
): PreviewReviewOutcome {
  // No runtime resolved at all — the caller's own case, not the review's.
  if (result === null) return { kind: 'unavailable', reviewKey }
  // Every give-up used to arrive as one bare `null`, so the screen blamed the
  // runtime even when the runtime advertised the skill and the capture was
  // what failed. Observed in the browser; a wrong cause is worse than none.
  if ('giveUp' in result) {
    return { kind: GIVE_UP_KIND[result.giveUp] ?? 'failed', reviewKey }
  }
  if (result.unauthorized) return { kind: 'unauthorized', reviewKey, status: result.status }
  if (!result.ok) return { kind: 'failed', reviewKey, status: result.status }
  return { kind: 'reviewed', reviewKey }
}
